import { Fragment } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import { handleLocaleUrl, GLOBAL_CLIENT_ONLY_LOCALE } from 'dibs-intl/exports/urls';

// components
import { FormattedMessage } from 'dibs-react-intl';
import { Button } from 'dibs-elements/exports/Button';
import { PdpSharedPurchaseButton } from '../PdpSharedButtons/PdpSharedPurchaseButton';
import { PdpSharedMakeOfferButton } from '../PdpSharedButtons/PdpSharedMakeOfferButton';
import { PdpSharedContactDealerButton } from '../PdpSharedButtons/PdpSharedContactDealerButton';
import { PdpSharedFreeShippingPillLazy } from '../PdpSharedFreeShippingPill/PdpSharedFreeShippingPillLazy';
import { PdpSharedRareFindPillLazy } from '../PdpSharedRareFindPill/PdpSharedRareFindPillLazy';
import { PdpSharedCondensedAuctionButtons } from '../PdpSharedCondensedAuctionButtons/PdpSharedCondensedAuctionButtons';
import { PdpSharedDeposit } from '../PdpSharedDeposit/PdpSharedDeposit';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';
import { FollowSearch } from '../pdpMessages';
import { SaveSearchButton } from '../../components/global/SaveSearchButton/SaveSearchButton';

// styles
import styles from './PdpSharedCondensedSidebarButtons.scss';

// helpers
import { trackEvent } from 'dibs-tracking';
import {
    trackFreeShipImpression,
    trackRareFindImpression,
} from '../tracking/sharedCondensedTracking';
import classnames from 'classnames';

const trackSeeFullDetails = () =>
    trackEvent({
        category: 'product',
        action: 'see full details clicked',
        label: 'landing pdp',
    });

const handleSeeFullDetails = (pdpUrl, selectedSkuId) => {
    trackSeeFullDetails();
    let modifiedPdpUrl = pdpUrl;
    if (selectedSkuId) {
        modifiedPdpUrl = `${pdpUrl}#skuId=${selectedSkuId}`;
    }
    window.location.assign(handleLocaleUrl(modifiedPdpUrl, GLOBAL_CLIENT_ONLY_LOCALE));
};

const DEFAULT_QUANTITY = '1';

const PdpSharedCondensedSidebarButtonsComponent = ({
    viewer,
    itemSearch,
    relayVariables,
    user,
    item,
    displayInfo,
    selectedSkuId,
}) => {
    const { itemId, userId, fetchAuction, isCondensedPdpRedesignVariant } = relayVariables;
    if (!displayInfo || !displayInfo.ctas) {
        return null;
    }

    const { saveSearchData, pdpURL, isSold, isUnpurchasable } = item || {};
    const { showRequestPrice, showMakeOffer, showFollowCategory, showPurchase } = displayInfo.ctas;
    const followSearchName = saveSearchData?.name;
    const followSearchPage = saveSearchData?.page;

    const inlineClassnames = classnames(styles.inlineButtons, {
        [styles.halfWidth]: showPurchase && showMakeOffer,
    });

    const isUnavailablePdp = isSold || isUnpurchasable;

    return (
        <div
            className={classnames(styles.buttons, {
                [styles.isCondensedPdpRedesignVariant]: isCondensedPdpRedesignVariant,
            })}
            data-tn="item-actions"
        >
            {!showFollowCategory && (
                <Button
                    type="primary"
                    className={styles.seeFullDetails}
                    dataTn="item-actions-view-details"
                    onClick={() => handleSeeFullDetails(pdpURL, selectedSkuId)}
                    size="medium"
                >
                    <FormattedMessage
                        id="PdpShareCondensedSidebarButtons.seeFullDetailsCta"
                        defaultMessage="View More Details"
                    />
                </Button>
            )}
            {!isCondensedPdpRedesignVariant && fetchAuction ? (
                // Place bid
                <PdpSharedCondensedAuctionButtons item={item} />
            ) : (
                <>
                    {!isCondensedPdpRedesignVariant && (
                        <div className={inlineClassnames}>
                            {/* Purchase */}
                            {!!showPurchase && (
                                <div className={styles.purchaseButton}>
                                    <PdpSharedPurchaseButton
                                        isPrimary={false}
                                        itemId={itemId}
                                        item={item}
                                        userId={userId}
                                        quantitySelected={DEFAULT_QUANTITY}
                                        size="medium"
                                        skuId={selectedSkuId}
                                    />
                                </div>
                            )}
                            {/* Make Offer */}
                            {!!showMakeOffer && (
                                <CurrencyProvider
                                    render={({ currency }) => (
                                        <div className={styles.makeOfferButton}>
                                            <PdpSharedMakeOfferButton
                                                itemId={itemId}
                                                item={item}
                                                userId={userId}
                                                user={user}
                                                quantitySelected={DEFAULT_QUANTITY}
                                                currency={currency}
                                                showArrow={false}
                                                size="medium"
                                                gaLabel="side rail cta"
                                            />
                                        </div>
                                    )}
                                />
                            )}
                        </div>
                    )}
                    {/* Request Price */}
                    {!isCondensedPdpRedesignVariant && !!showRequestPrice && (
                        <PdpSharedContactDealerButton
                            type={'REQUEST_PRICE'}
                            item={item}
                            user={user}
                            buttonType="secondary"
                            size="medium"
                        />
                    )}
                    {/* Rare Find and Shipping pills */}
                    {isCondensedPdpRedesignVariant && !isUnavailablePdp && (
                        <div className={styles.badgeContainer}>
                            <PdpSharedRareFindPillLazy
                                item={item}
                                backgroundColor="moonstone"
                                hideIcon
                                size="small-heavy"
                                trackImpression={trackRareFindImpression}
                            />
                            <CurrencyProvider
                                render={({ currency }) => (
                                    <PdpSharedFreeShippingPillLazy
                                        viewer={viewer}
                                        item={item}
                                        currency={currency}
                                        trackImpression={trackFreeShipImpression}
                                    />
                                )}
                            />
                        </div>
                    )}
                </>
            )}
            <PdpSharedDeposit userId={userId} item={item} />

            {showFollowCategory && (
                <Fragment>
                    <p className={styles.followSearchText}>
                        <FollowSearch
                            searchName={followSearchName}
                            searchNameClassname={styles.searchName}
                        />
                    </p>
                    <SaveSearchButton
                        userId={userId}
                        user={user}
                        favoritesConnection={user?.favorites || null}
                        page={followSearchPage}
                        name={followSearchName}
                        size="medium"
                        type="primary"
                        itemSearch={itemSearch}
                    />
                </Fragment>
            )}
        </div>
    );
};

PdpSharedCondensedSidebarButtonsComponent.propTypes = {
    relayVariables: PropTypes.object.isRequired,
    viewer: PropTypes.object,
    itemSearch: PropTypes.object,
    user: PropTypes.object,
    item: PropTypes.object.isRequired,
    userId: PropTypes.string,
    displayInfo: PropTypes.object,
    selectedSkuId: PropTypes.string,
};

export const PdpSharedCondensedSidebarButtons = createFragmentContainer(
    PdpSharedCondensedSidebarButtonsComponent,
    {
        viewer: graphql`
            fragment PdpSharedCondensedSidebarButtons_viewer on Viewer {
                ...PdpSharedFreeShippingPill_viewer @include(if: $isCondensedPdpRedesignVariant)
            }
        `,
        user: graphql`
            fragment PdpSharedCondensedSidebarButtons_user on User {
                ...PdpSharedMakeOfferButton_user
                ...PdpSharedContactDealerButton_user
                favorites(first: 1, types: [SEARCH, USER_PREFERENCE], pages: $followSearchPages)
                    @include(if: $fetchFollowSearch) {
                    ...SaveSearchButton_favoritesConnection
                }
            }
        `,
        item: graphql`
            fragment PdpSharedCondensedSidebarButtons_item on Item {
                ...PdpSharedPurchaseButton_item
                ...PdpSharedFreeShippingPill_item @include(if: $isCondensedPdpRedesignVariant)
                ...PdpSharedRareFindPill_item @include(if: $isCondensedPdpRedesignVariant)
                ...PdpSharedBlurbRareFind_item @include(if: $isCondensedPdpRedesignVariant)
                ...PdpSharedContactDealerButton_item
                ...PdpSharedMakeOfferButton_item
                ...PdpSharedDeposit_item
                ...PdpSharedCondensedAuctionButtons_item @include(if: $fetchAuction)
                pdpURL
                isSold
                isUnpurchasable
                saveSearchData {
                    name
                    page
                }
            }
        `,
        itemSearch: graphql`
            fragment PdpSharedCondensedSidebarButtons_itemSearch on ItemSearchQueryConnection {
                ...SaveSearchButton_itemSearch
            }
        `,
        displayInfo: graphql`
            fragment PdpSharedCondensedSidebarButtons_displayInfo on ItemDisplayInfo {
                ctas {
                    showPurchase
                    showMakeOffer
                    showFollowCategory
                    showRequestPrice
                }
            }
        `,
    }
);
